import { useAuth } from "@/context/AuthContext";
import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Users, Upload, Pencil, Trash2, Search, FileSpreadsheet, Mail, Building2, History, Undo2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { RuntsCerca } from "@/components/RuntsCerca";
import { RuntsBadge } from "@/components/RuntsBadge";
import { Link } from "react-router-dom";

const EMPTY = {
  ragione_sociale: "", codice_fiscale: "", partita_iva: "", forma_giuridica: "",
  tipologia_ente: "", indirizzo: "", cap: "", comune: "", provincia: "",
  email: "", pec: "", cellulare: "", tel_lavoro: "", referente: "", sito_web: "", note: "",
};

const euroNum = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function Anagrafiche() {
  const { user: _uDop } = useAuth();
  const isAdminDop = _uDop?.role === "admin";
  const [nDoppioni, setNDoppioni] = useState(0);
  useEffect(() => { if (isAdminDop) api.get("/anagrafiche-doppioni").then(({ data }) => setNDoppioni(data.filter((g) => !g.piva_diverse).length)).catch(() => {}); }, [isAdminDop]);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [batchesOpen, setBatchesOpen] = useState(false);
  const [batches, setBatches] = useState([]);
  const fileRef = useRef();

  const loadBatches = useCallback(() => {
    api.get("/anagrafiche/import-batches").then(({ data }) => setBatches(data)).catch(() => setBatches([]));
  }, []);

  const load = useCallback((q = "") => {
    api.get("/anagrafiche", { params: { ...(q ? { q } : {}), limit: 2000 } }).then(({ data }) => setItems(data));
    api.get("/anagrafiche/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); loadBatches(); }, [load, loadBatches]);
  useEffect(() => {
    const t = setTimeout(() => load(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [pivaBusy, setPivaBusy] = useState(false);
  const lookupPiva = async () => {
    const piva = (form.partita_iva || form.codice_fiscale || "").trim();
    if (!piva) { toast.error("Inserisci prima la Partita IVA o il codice fiscale dell'ente"); return; }
    setPivaBusy(true);
    try {
      const { data: r } = await api.get("/ai/lookup-piva", { params: { piva } });
      if (!r.disponibile) { toast.error(r.motivo || "Dati camerali non disponibili"); return; }
      setForm((f) => ({
        ...f,
        ...(r.runts ? { codice_fiscale: r.runts.codice_fiscale, provincia: r.runts.provincia || f.provincia, tipologia_ente: f.tipologia_ente || r.runts.sezione,
          referente: f.referente || r.runts.legale_rappresentante } : {}),
        ragione_sociale: r.ragione_sociale || f.ragione_sociale,
        comune: r.comune || f.comune,
        forma_giuridica: r.natura_giuridica || f.forma_giuridica,
      }));
      toast.success(`Compilato da ${r.runts ? "RUNTS" : "Registro Imprese"}: ${r.ragione_sociale}${r.comune ? " · " + r.comune : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verifica non riuscita"); }
    finally { setPivaBusy(false); }
  };
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (o) => { setEditing(o); setForm({ ...EMPTY, ...o }); setOpen(true); };

  const remove = async (o) => {
    if (!window.confirm(`Eliminare "${o.ragione_sociale}" dall'anagrafica condivisa?`)) return;
    try { await api.delete(`/anagrafiche/${o.id}`); toast.success("Anagrafica eliminata."); load(search.trim()); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/anagrafiche/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import completato: ${data.created} nuovi, ${data.updated} aggiornati, ${data.invariati ?? 0} invariati, ${data.skipped} ignorati.`);
      load(); loadBatches();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore durante l'import");
    } finally { setImporting(false); e.target.value = ""; }
  };

  const rollbackImport = async (b) => {
    if (!window.confirm(`Annullare l'import "${b.filename}"?\nVerranno eliminate ${b.created} anagrafiche create e ripristinati ${b.updated} aggiornamenti.`)) return;
    try {
      const { data } = await api.delete(`/anagrafiche/import-batches/${b.id}`);
      toast.success(`Import annullato: ${data.deleted} eliminate, ${data.reverted} ripristinate.`);
      loadBatches(); load(search.trim());
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore durante l'annullamento"); }
  };

  const fmtDate = (s) => { try { return new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };

  const save = async () => {
    if (!form.ragione_sociale) { toast.error("Ragione sociale obbligatoria."); return; }
    try {
      if (editing) { await api.put(`/anagrafiche/${editing.id}`, form); toast.success("Anagrafica aggiornata."); }
      else { await api.post("/anagrafiche", form); toast.success("Anagrafica creata."); }
      setOpen(false); setForm(EMPTY); setEditing(null); load(search.trim());
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const StatBox = ({ icon: Icon, label, value, color }) => (
    <div className="rounded-xl bg-white border border-slate-200 p-4 flex items-center gap-3 shadow-sm">
      <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}1A`, color }}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="font-heading text-2xl font-semibold text-slate-900">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="anagrafiche-page">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Anagrafica Unica</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Anagrafiche</h1>
            <p className="text-slate-500 mt-2 max-w-2xl">Registro condiviso tra Quotatore RC, Cauzioni Pro e MUP. Import da CSV Assieasy in attesa del collegamento all'API CRM ufficiale.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline"><Link to="/anagrafiche/prospect-terzo-settore" data-testid="ana-prospect-btn">Prospect Terzo Settore</Link></Button>
            {isAdminDop && <Button asChild variant="outline"><Link to="/anagrafiche/doppioni" data-testid="ana-doppioni-btn">Anagrafiche doppie{nDoppioni ? ` (${nDoppioni})` : ""}</Link></Button>}
            <input ref={fileRef} type="file" accept=".csv" onChange={onImport} className="hidden" data-testid="ana-import-input" />
            <Button data-testid="ana-import-btn" variant="outline" disabled={importing} onClick={() => fileRef.current?.click()}>
              <Upload className="w-4 h-4 mr-1.5" /> {importing ? "Import in corso…" : "Importa CSV (Assieasy)"}
            </Button>
            <Dialog open={batchesOpen} onOpenChange={setBatchesOpen}>
              <DialogTrigger asChild>
                <Button data-testid="ana-imports-btn" variant="outline" onClick={loadBatches}>
                  <History className="w-4 h-4 mr-1.5" /> Importazioni{batches.length ? ` (${batches.length})` : ""}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl" data-testid="ana-imports-dialog">
                <DialogHeader>
                  <DialogTitle>Storico importazioni</DialogTitle>
                  <DialogDescription>Annulla un'importazione eseguita per errore: verranno eliminate le anagrafiche create e ripristinati i campi riempiti sugli aggiornamenti.</DialogDescription>
                </DialogHeader>
                <div className="max-h-[55vh] overflow-y-auto space-y-2">
                  {batches.length === 0 ? (
                    <div className="text-center text-slate-400 py-10 text-sm">Nessuna importazione registrata.</div>
                  ) : batches.map((b) => (
                    <div key={b.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3" data-testid={`ana-batch-${b.id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-800 truncate">
                          <FileSpreadsheet className="h-4 w-4 text-slate-400 shrink-0" />{b.filename || "Import CSV"}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {fmtDate(b.created_at)} · <b className="text-emerald-600">{b.created}</b> nuovi · <b className="text-blue-600">{b.updated}</b> aggiornati · {b.invariati ?? 0} invariati · {b.skipped} ignorati
                        </div>
                      </div>
                      <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 shrink-0" onClick={() => rollbackImport(b)} data-testid={`ana-rollback-${b.id}`}>
                        <Undo2 className="h-3.5 w-3.5 mr-1" />Annulla
                      </Button>
                    </div>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
              <DialogTrigger asChild>
                <Button data-testid="ana-new-btn" onClick={openNew} className="brand-gradient text-white border-0">
                  <Plus className="w-4 h-4 mr-1.5" /> Nuova anagrafica
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editing ? "Modifica anagrafica" : "Nuova anagrafica"}</DialogTitle>
                  <DialogDescription>Dati anagrafici condivisi tra le app della Suite.</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
                  {[
                    ["ragione_sociale", "Ragione sociale *", 2],
                    ["codice_fiscale", "Codice fiscale", 1],
                    ["partita_iva", "Partita IVA", 1],
                    ["forma_giuridica", "Forma giuridica", 1],
                    ["tipologia_ente", "Tipologia ente", 1],
                    ["indirizzo", "Indirizzo", 2],
                    ["cap", "CAP", 1],
                    ["comune", "Comune", 1],
                    ["provincia", "Provincia", 1],
                    ["referente", "Referente / Legale rappr.", 1],
                    ["email", "Email", 1],
                    ["pec", "PEC", 1],
                    ["cellulare", "Cellulare", 1],
                    ["tel_lavoro", "Tel. lavoro", 1],
                    ["sito_web", "Sito web (per profilo AI)", 2],
                  ].map(([k, label, span]) => (
                    <div key={k} className={span === 2 ? "col-span-2" : ""}>
                      <Label className="text-slate-600 text-sm">{label}</Label>
                      <Input data-testid={`ana-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1" />
                      {k === "codice_fiscale" && (
                        <div className="mt-1.5 space-y-1"><RuntsBadge cf={form.codice_fiscale || form.partita_iva} testid="ana-form-runts" /><RuntsCerca onScegli={(e) => setForm((f) => ({ ...f, ragione_sociale: e.denominazione, codice_fiscale: e.codice_fiscale,
                          comune: e.comune_tc, provincia: e.provincia, tipologia_ente: f.tipologia_ente || e.sezione_tc, referente: f.referente || e.legale_rappresentante }))} label="Cerca ETS nel RUNTS" /></div>
                      )}
                      {k === "partita_iva" && (
                        <button type="button" onClick={lookupPiva} disabled={pivaBusy}
                          className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 hover:text-indigo-900 disabled:opacity-50" data-testid="ana-piva-lookup">
                          {pivaBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Building2 className="h-3.5 w-3.5" />} Verifica P.IVA / C.F. e compila (Registro Imprese o RUNTS)
                        </button>
                      )}
                    </div>
                  ))}
                  <div className="col-span-2">
                    <Label className="text-slate-600 text-sm">Note</Label>
                    <Textarea data-testid="ana-note" value={form.note || ""} onChange={(e) => set("note", e.target.value)} rows={2} className="mt-1" />
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="ana-save-btn" onClick={save} className="brand-gradient text-white border-0">Salva</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatBox icon={Users} label="Totale anagrafiche" value={stats?.totale ?? "—"} color="#1B2380" />
          <StatBox icon={FileSpreadsheet} label="Importate da CSV" value={stats?.da_csv ?? "—"} color="#10B981" />
          <StatBox icon={Building2} label="Inserite a mano" value={stats?.manuali ?? "—"} color="#8B5CF6" />
          <StatBox icon={Mail} label="Con email" value={stats?.con_email ?? "—"} color="#F97316" />
        </div>

        <div className="relative mb-4 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="ana-search" placeholder="Cerca per ragione sociale, CF, P.IVA, comune o email…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white" />
        </div>
        <p className="text-xs text-slate-400 mb-3" data-testid="ana-count">{items.length} risultati</p>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <th className="text-left font-medium px-4 py-3">Ragione sociale</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">CF / P.IVA</th>
                <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">Comune</th>
                <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">Contatti</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => (
                <tr key={o.id} data-testid={`ana-row-${o.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{o.ragione_sociale}</div>
                    <div className="text-xs text-slate-400">{o.tipologia_ente || o.tipo_cliente || "—"}</div>
                    {o.runts && <div className="mt-0.5"><RuntsBadge runts={o.runts} testid={`ana-runts-${o.id}`} /></div>}
                  </td>
                  <td className="px-4 py-3 font-mono text-slate-600 hidden md:table-cell">{o.partita_iva || o.codice_fiscale || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">{o.comune ? `${o.comune}${o.provincia ? ` (${o.provincia})` : ""}` : "—"}</td>
                  <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">
                    <div className="truncate max-w-[220px]">{o.email || "—"}</div>
                    {o.telefono && <div className="text-xs text-slate-400 font-mono">{o.telefono}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid={`ana-edit-${o.id}`} variant="ghost" size="icon" onClick={() => openEdit(o)} className="h-8 w-8 text-slate-500 hover:text-slate-900">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button data-testid={`ana-delete-${o.id}`} variant="ghost" size="icon" onClick={() => remove(o)} className="h-8 w-8 text-slate-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">Nessuna anagrafica. Importa il CSV Assieasy o creane una nuova.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
